import { rem } from 'polished'
import type { RefObject } from 'react'
import { useEffect, useRef } from 'react'
import { css } from 'styled-components'

/*
 * Read from a private module deliberately: the sheet publishes its own height
 * ceiling as a custom property and nothing re-exports the name. Importing it
 * rather than repeating the literal means a rename in the design system breaks
 * the build here instead of silently letting a panel outgrow its sheet.
 */
import { DROPDOWN_SHEET_MAX_HEIGHT_CSS_VARIABLE } from 'LuxKit/Dropdown/_DropdownSheet'

/** Tall enough for a six-week month — the most any month needs. */
const FULL_MONTH_HEIGHT_PX = 480

/**
 * The one scroller a date panel gets.
 *
 * Bounded by the sheet's own published maximum rather than a fixed height:
 * a sheet sizes itself to the room below the field it hangs off, so anything
 * taller would make the sheet scroll as well and put a second scrollbar
 * beside this one.
 *
 * `chromeHeightPx` is what the panel keeps for itself — the toggle or deal
 * banner above the calendar, the footer below it, and the sheet's padding.
 */
export function calendarScrollerStyles(chromeHeightPx: number) {
  return css`
    width: 100%;
    min-width: 0;
    max-height: min(
      ${rem(FULL_MONTH_HEIGHT_PX)},
      calc(var(${DROPDOWN_SHEET_MAX_HEIGHT_CSS_VARIABLE}, 100dvh) - ${rem(chromeHeightPx)})
    );
    overflow-y: auto;
    overscroll-behavior: contain;
  `
}

/**
 * Marks a month header this module rendered, so the aligner can find a month
 * without walking the calendar's DOM. The grid renders each month as a flat
 * label-then-weeks pair with no wrapper, so there is nothing else to anchor on.
 */
export const TRIP_MONTH_ATTRIBUTE = 'data-trip-month'

/** Fallback when the weekday row cannot be measured. */
const WEEKDAY_ROW_FALLBACK_PX = 44
/** Layout can settle a frame or two late, so the alignment is retried. */
const MAX_ALIGN_ATTEMPTS = 5

function findMonthHeader(container: HTMLElement, month: string): HTMLElement | null {
  return container.querySelector<HTMLElement>(`[${TRIP_MONTH_ATTRIBUTE}="${month}"]`)
}

/**
 * Brings a month to the top of a scrolling calendar.
 *
 * Deliberately not `scrollIntoView`: that walks up every scrollable ancestor,
 * so the panel would scroll its own header — the date-mode toggle, or a deal
 * banner — out of view along with it. Setting `scrollTop` moves this one
 * container and nothing else.
 *
 * Scrolling down off the top matters for its own sake too: the mounted window
 * grows upwards whenever its top sentinel is in view, so a calendar left at
 * the very top quietly pages in the months before the one it was asked for.
 */
export function useAlignedMonth(
  month: string | undefined,
  scrollContainerRef: RefObject<HTMLElement | null> | undefined,
  enabled = true,
) {
  const alignedRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!enabled || !month) return
    if (alignedRef.current === month) return

    const container = scrollContainerRef?.current
    if (!container) return

    let attemptsLeft = MAX_ALIGN_ATTEMPTS
    let frame = 0

    const align = () => {
      const monthHeader = findMonthHeader(container, month)
      if (monthHeader) {
        const weekdayRowHeight = container.querySelector<HTMLElement>('[aria-hidden="true"]')?.offsetHeight
          ?? WEEKDAY_ROW_FALLBACK_PX
        const correction = monthHeader.getBoundingClientRect().top -
          container.getBoundingClientRect().top -
          weekdayRowHeight

        if (Math.abs(correction) <= 1) {
          alignedRef.current = month
          return
        }
        container.scrollTop += correction
      }

      attemptsLeft -= 1
      if (attemptsLeft > 0) frame = window.requestAnimationFrame(align)
    }

    frame = window.requestAnimationFrame(align)
    return () => window.cancelAnimationFrame(frame)
  }, [month, scrollContainerRef, enabled])
}
